import React, { useRef, useEffect, useState } from "react";
import "./ContactUsPage.css";
import { httpsCallable } from "firebase/functions";
import { functionsME, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faInstagram, faTwitter, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import { useLanguage } from "../../../context/LanguageContext";
import SkeletonLoader from "../../../components/common/SkeletonLoader/SkeletonLoader";

const ContactUsPage = () => {
  const form = useRef();
  const cardRef = useRef();
  const formRef = useRef();
  const infoRef = useRef();

  const [visible, setVisible] = useState({
    card: true,
    form: true,
    info: true,
  });

  // Get store settings for social media and contact info
  const { storeSettings, loading } = useStoreSettings(DEFAULT_STORE_ID);
  const { language } = useLanguage();

  // Translations for contact page
  const translations = {
    en: {
      contactUs: "Contact Us",
      getInTouch: "Get In Touch",
      description: "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
      yourName: "Your Name",
      yourEmail: "Your Email",
      subject: "Subject",
      message: "Message",
      sendMessage: "Send Message",
      contactInfo: "Contact Information",
      visitUs: "Visit Us",
      callUs: "Call Us",
      emailUs: "Email Us",
      messageSentSuccess: "Message sent successfully!",
      messageSentError: "Something went wrong. Please try again.",
      noSocialMedia: "No social media links configured"
    },
    ar: {
      contactUs: "اتصل بنا",
      getInTouch: "تواصل معنا",
      description: "نحن نحب أن نسمع منك. أرسل لنا رسالة وسنرد في أقرب وقت ممكن.",
      yourName: "اسمك",
      yourEmail: "بريدك الإلكتروني",
      subject: "الموضوع",
      message: "الرسالة",
      sendMessage: "إرسال الرسالة",
      contactInfo: "معلومات الاتصال",
      visitUs: "زورنا",
      callUs: "اتصل بنا",
      emailUs: "راسلنا",
      messageSentSuccess: "تم إرسال الرسالة بنجاح!",
      messageSentError: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
      noSocialMedia: "لم يتم تكوين روابط وسائل التواصل الاجتماعي"
    }
  };

  const t = translations[language] || translations.en;

  // Add debug logging
  useEffect(() => {
    // console.log("ContactUsPage - Store settings:", storeSettings);
    // console.log("ContactUsPage - Loading:", loading);
    // console.log("ContactUsPage - Social Media data:", storeSettings?.socialMedia);
  }, [storeSettings, loading]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target === cardRef.current) setVisible(prev => ({ ...prev, card: true }));
            if (entry.target === formRef.current) setVisible(prev => ({ ...prev, form: true }));
            if (entry.target === infoRef.current) setVisible(prev => ({ ...prev, info: true }));
          }
        });
      },
      { threshold: 0.2 }
    );

    [cardRef, formRef, infoRef].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      [cardRef, formRef, infoRef].forEach(ref => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  const sendEmail = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData(form.current);
      const sendContactForm = httpsCallable(functionsME, 'sendContactFormEmail');

      console.log('Sending contact form with storeId:', DEFAULT_STORE_ID);

      const result = await sendContactForm({
        name: formData.get('user_name'),
        email: formData.get('user_email'),
        subject: formData.get('subject'),
        message: formData.get('message'),
        storeId: DEFAULT_STORE_ID
      });


      console.log('Contact form sent successfully:', result);
      alert(t.messageSentSuccess);
      form.current.reset();
    } catch (error) {
      console.error('Error sending contact form:', error);
      console.error('Error details:', error.code, error.message);
      alert(t.messageSentError);
    }
  };

  // Get social media icon based on platform
  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return faFacebookF;
      case 'instagram':
        return faInstagram;
      case 'twitter':
        return faTwitter;
      case 'linkedin':
        return faLinkedinIn;
      default:
        return null;
    }
  };

  // Render social media links
  const renderSocialIcons = () => {
    console.log("Rendering social icons, socialMedia:", storeSettings?.socialMedia);

    if (!storeSettings?.socialMedia) {
      console.log("No social media data found");
      return (
        <div className="no-social-media">
          <p>{t.noSocialMedia}</p>
        </div>
      );
    }

    const socialEntries = Object.entries(storeSettings.socialMedia);
    console.log("All social entries:", socialEntries);

    const validEntries = socialEntries.filter(([platform, url]) => {
      console.log(`Checking ${platform}: "${url}" (length: ${url?.length})`);
      return url && url.trim() !== "";
    });

    console.log("Valid social entries:", validEntries);



    return validEntries.map(([platform, url]) => {
      const icon = getSocialIcon(platform);
      if (!icon) {
        console.log(`No icon found for platform: ${platform}`);
        return null;
      }

      return (
        <a
          key={platform}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visit our ${platform} page`}
          className="social-icon-link"
        >
          <FontAwesomeIcon icon={icon} />
        </a>
      );
    });
  };

  if (loading) {
    return (
      <div className="contact-section">
        <SkeletonLoader type="contact" className="contact-skeleton" />
      </div>
    );
  }

  return (
    <div className="contact-section">
      <div className="contact-cards">
        <div
          className={`card fade-in-up ${visible.card ? "visible" : ""}`}
          ref={cardRef}
        >
          <FontAwesomeIcon icon={faPhone} />
          <h4>{t.callUs}</h4>
          <p>{storeSettings?.phone || (language === 'ar' ? "الهاتف غير متوفر" : "Phone not available")}</p>
        </div>

        <div
          className={`card fade-in-up ${visible.card ? "visible" : ""}`}
        >
          <FontAwesomeIcon icon={faEnvelope} />
          <h4>{t.emailUs}</h4>
          <p>{storeSettings?.email || (language === 'ar' ? "البريد الإلكتروني غير متوفر" : "Email not available")}</p>
        </div>
      </div>

      <div className="contact-main">
        <form
          ref={form}
          onSubmit={sendEmail}
          className={`contact-form fade-in-up ${visible.form ? "visible" : ""}`}
        >
          <input
            type="text"
            name="user_name"
            placeholder={t.yourName}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            required
          />
          <input
            type="email"
            name="user_email"
            placeholder={t.yourEmail}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            required
          />
          <input
            type="text"
            name="subject"
            placeholder={t.subject}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            required
          />
          <textarea
            name="message"
            placeholder={t.message}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            required
          />
          <button type="submit">{t.sendMessage}</button>
        </form>

        <div
          className={`contact-info fade-in-up ${visible.info ? "visible" : ""}`}
          ref={infoRef}
        >
          <h2>{t.getInTouch}</h2>
          <p>
            <em>
              {storeSettings?.description
              }
            </em>
          </p>
          <div className="social-icons">
            {renderSocialIcons()}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
